import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/Header";
import { NoBusinessGuard } from "@/components/NoBusinessGuard";
import {
  ClockCounterClockwise,
  Warning,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

export default function DashboardPage() {
  const { api, user } = useAuth();
  const [briefs, setBriefs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    const load = async () => {
      const [briefResp, draftResp, approvalResp, usageResp] = await Promise.allSettled([
        api.get("/briefs"),
        api.get("/history?limit=5&page=1"),
        api.get("/approvals"),
        api.get("/usage"),
      ]);

      if (briefResp.status === "fulfilled") setBriefs(briefResp.value.data?.briefs || []);
      if (draftResp.status === "fulfilled") setDrafts(draftResp.value.data?.items || []);
      if (approvalResp.status === "fulfilled") setApprovals(approvalResp.value.data?.approval_links || []);
      if (usageResp.status === "fulfilled") setUsage(usageResp.value.data || null);
    };

    load();
  }, [api]);

  const todayTasks = useMemo(() => {
    const dueApprovals = approvals.filter((link) => link.is_active).length;
    const pendingBriefs = briefs.filter((brief) => brief.status !== "used").length;
    const milestones = user?.onboarding?.milestones || {};
    const hasStarterChat = !!milestones.starter_chat_started;
    const hasFirstBrief = !!milestones.first_brief_created;
    const hasQualityCheck = !!milestones.first_draft_quality_checked;

    return [
      {
        label: hasStarterChat ? "Keep brainstorming in chat" : "Start guided brainstorm chat",
        detail: hasStarterChat ? "Capture your next angle from chat" : "Use starter prompts to find your direction",
        to: "/chat?starter=1",
      },
      {
        label: hasFirstBrief ? "Finish open briefs" : "Create your first brief",
        detail: hasFirstBrief ? (pendingBriefs > 0 ? `${pendingBriefs} draft brief${pendingBriefs !== 1 ? "s" : ""}` : "All briefs completed") : "Optional — helps generate better content",
        to: hasFirstBrief ? "/briefs" : "/briefs/new",
      },
      {
        label: hasQualityCheck ? "Create today's draft" : "Run first-draft quality check",
        detail: hasQualityCheck ? "Use chat-first generation to ship faster" : "Complete voice/strategy checklist on your first saved draft",
        to: "/chat",
      },
      {
        label: "Review pending approvals",
        detail: dueApprovals > 0 ? `${dueApprovals} active link${dueApprovals !== 1 ? "s" : ""}` : "No active approvals",
        to: "/approvals",
      },
    ];
  }, [approvals, briefs, user]);

  const usageAlert = useMemo(() => {
    if (!usage || usage.is_admin) return "No usage alerts.";
    if (usage.generations_limit) {
      const ratio = usage.generations_used / usage.generations_limit;
      if (ratio >= 1) return "Generation limit reached — upgrade to keep creating.";
      if (ratio >= 0.8) return "You are above 80% of monthly generation usage.";
      return "Usage is healthy.";
    }
    if (usage.fair_use_warning) return "Fair-use warning active. Usage is nearing the threshold.";
    return "No usage alerts.";
  }, [usage]);

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Header />
      <NoBusinessGuard>
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
          <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-5 sm:p-6">
            <p className="text-xs uppercase tracking-wider text-[#FF4400] font-semibold">Dashboard</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-[var(--text-primary)] font-['Cormorant_Garamond'] mt-1">Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">Your operational home for planning, drafting, and approvals.</p>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">Today's tasks</h2>
              <div className="space-y-2">
                {todayTasks.map((task) => (
                  <Link key={task.label} to={task.to} className="flex items-start justify-between gap-3 p-2.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors">
                    <div>
                      <p className="text-sm text-[var(--text-primary)]">{task.label}</p>
                      <p className="text-xs text-[var(--text-muted)] mt-0.5">{task.detail}</p>
                    </div>
                    <span className="text-[#FF4400] text-xs">Open</span>
                  </Link>
                ))}
              </div>
            </section>

            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">Active briefs</h2>
              <div className="space-y-2">
                {briefs.slice(0, 4).map((brief) => (
                  <Link key={brief.brief_id} to={`/briefs/${brief.brief_id}`} className="flex items-center justify-between p-2.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors">
                    <div>
                      <p className="text-sm text-[var(--text-primary)] truncate">{brief.title}</p>
                      <p className="text-xs text-[var(--text-muted)]">{brief.status || "draft"}</p>
                    </div>
                    <span className="text-xs text-[var(--text-muted)]">{formatDate(brief.created_at)}</span>
                  </Link>
                ))}
                {briefs.length === 0 && <p className="text-sm text-[var(--text-muted)]">No briefs yet. Start by creating one.</p>}
              </div>
            </section>

            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">Recent drafts</h2>
              <div className="space-y-2">
                {drafts.slice(0, 4).map((draft) => (
                  <Link key={draft.history_id} to="/history" className="flex items-center justify-between p-2.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors">
                    <div>
                      <p className="text-sm text-[var(--text-primary)] truncate">{draft.title || draft.topic || "Untitled draft"}</p>
                      <p className="text-xs text-[var(--text-muted)]">{draft.content_type || "General"}</p>
                    </div>
                    <ClockCounterClockwise size={14} className="text-[var(--text-muted)]" />
                  </Link>
                ))}
                {drafts.length === 0 && <p className="text-sm text-[var(--text-muted)]">No recent drafts yet.</p>}
              </div>
            </section>

            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">Approvals due</h2>
              <div className="space-y-2">
                {approvals.slice(0, 4).map((link) => (
                  <Link key={link.link_id} to="/approvals" className="flex items-center justify-between p-2.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors">
                    <div>
                      <p className="text-sm text-[var(--text-primary)] truncate">{link.title}</p>
                      <p className="text-xs text-[var(--text-muted)]">{link.views || 0} views · {link.is_active ? "active" : "inactive"}</p>
                    </div>
                    <span className="text-xs text-[var(--text-muted)]">{formatDate(link.created_at)}</span>
                  </Link>
                ))}
                {approvals.length === 0 && <p className="text-sm text-[var(--text-muted)]">No approvals waiting right now.</p>}
              </div>
            </section>

            <section className="bg-[var(--bg-card)] border border-[var(--border)] rounded-lg p-5 lg:col-span-2">
              <h2 className="text-base font-semibold text-[var(--text-primary)] mb-3">Usage alerts</h2>
              <div className="flex items-start gap-2.5 rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
                <Warning size={18} className="text-amber-400 mt-0.5" />
                <p className="text-sm text-[var(--text-secondary)]">{usageAlert}</p>
              </div>
            </section>
          </div>
        </main>
      </NoBusinessGuard>
    </div>
  );
}
